import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { useUpdateParent } from '../../api/parent/parentQueries.js';
import { toast } from 'react-toastify';
import { SlidePanel } from '../ui/slide-panel';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';

export function ParentEditPanel({ parent, isOpen, onClose }) {
  const { mutate: updateParent, isPending: isUpdating } = useUpdateParent();
  const [parentId, setParentId] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [photoUrl, setPhotoUrl] = useState("/placeholder.svg");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (parent) {
      // Extract and store parent ID
      const id = parent.id || parent.parentId;
      setParentId(id);
      
      // Set form fields from parent data
      setFirstName(parent.firstName || "");
      setLastName(parent.lastName || "");
      setEmail(parent.email || "");
      setPhone(parent.phoneNumber || parent.phone || "");
      setAddress(parent.address || "");
      setPhotoUrl(parent.photoUrl || parent.avatar || "/placeholder.svg");

      // Debug log for verification
      console.log('Initializing edit form with data:', {
        id,
        firstName: parent.firstName,
        lastName: parent.lastName,
        email: parent.email,
        phone: parent.phoneNumber || parent.phone,
        address: parent.address,
        photoUrl: parent.photoUrl || parent.avatar
      });
    }
  }, [parent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!parentId) {
      toast.error('Parent ID is missing');
      console.error('Parent ID missing. Current state:', { 
        parentId,
        parentProp: parent,
        'parent?.id': parent?.id,
        'parent?.parentId': parent?.parentId
      });
      return;
    }

    const data = {
      firstName,
      lastName,
      email,
      phoneNumber: phone,
      address,
      photoUrl
    };
    
    console.log('Submitting update with data:', data);
    
    updateParent([parentId, data], {
      onSuccess: () => {
        toast.success('Parent details updated successfully');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update parent details');
      }
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangeImage = () => {
    fileInputRef.current?.click();
  };

  const renderFooter = () => (
    <div className="flex justify-center gap-4">
      <Button 
        type="button" 
        variant="outline" 
        className="w-full sm:w-40 text-purple-500 border-purple-200"
        onClick={onClose}
        disabled={isUpdating}
      >
        Cancel
      </Button>
      <Button 
        type="submit" 
        form="parent-edit-form"
        className="w-full sm:w-40 bg-purple-500 hover:bg-purple-600 text-white"
        disabled={isUpdating}
      >
        {isUpdating ? (
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" className="text-white" />
            Saving...
          </div>
        ) : (
          'Save Changes'
        )}
      </Button>
    </div>
  );

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Parent Details"
      footer={renderFooter()}
    >
      <form 
        id="parent-edit-form" 
        onSubmit={handleSubmit} 
        className="h-full flex flex-col"
        autoComplete="on"
      >
        <div className="flex-1 p-6 space-y-8">
          <input
            type="file"
            id="parent-photo-upload"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleImageSelect}
            aria-label="Upload parent photo"
          />
          
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-200">
                <img
                  src={photoUrl}
                  alt="Parent profile"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
            <Button 
              type="button"
              className="bg-purple-500 hover:bg-purple-600 text-white" 
              onClick={handleChangeImage}
              aria-label="Change profile image"
            >
              Change Image
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="parent-first-name">First Name</Label>
              <Input
                id="parent-first-name"
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent-last-name">Last Name</Label>
              <Input
                id="parent-last-name"
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-email">Email</Label>
            <Input
              id="parent-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-phone">Phone Number</Label>
            <Input
              id="parent-phone"
              name="phoneNumber"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoComplete="tel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-address">Address</Label>
            <Input
              id="parent-address"
              name="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              autoComplete="street-address"
            />
          </div>
        </div>
      </form>
    </SlidePanel>
  );
}